/**
 * Competitor Analysis Service
 * 
 * Deep Research for regional competitor analysis.
 * Combines Google Places API with Firecrawl scraping and Gemini analysis.
 */

import { db } from '../config/firebase';
import { 
  collection, 
  doc, 
  setDoc, 
  getDoc, 
  getDocs, 
  query, 
  orderBy, 
  Timestamp,
  limit
} from 'firebase/firestore';
import { GoogleGenAI } from '@google/genai';
import { placesService } from './placesService';
import { firecrawlService } from './firecrawlService';
import type { BrandBook } from '../types/brandBook';
import type { 
  CompetitorProfile, 
  CompetitorComparison, 
  ComparisonRow,
  CompetitorInsights,
  OwnProfile,
  AnalysisProgress,
  PricingTier,
  SavedCompetitorAnalysis
} from '../types/competitor';

/**
 * Analyze competitor website content with Gemini
 */
async function analyzeCompetitorWithAI(
  markdown: string,
  html: string,
  name: string
): Promise<Partial<CompetitorProfile>> {
  try {
    const apiKey = import.meta.env.VITE_GEMINI_API_KEY;
    
    if (!apiKey) {
      console.warn('Gemini API key not configured, using basic analysis');
      return extractBasicInfo(markdown, name);
    }
    
    const genAI = new GoogleGenAI({ apiKey });
    
    const response = await genAI.models.generateContent({
      model: 'gemini-2.0-flash',
      contents: [{
        parts: [{
          text: `Analysiere diese Autohaus-Website für eine Wettbewerbsanalyse.

WEBSITE-INHALTE:
${markdown.substring(0, 30000)}

Extrahiere folgende Informationen und antworte NUR mit einem JSON-Objekt (keine Markdown-Formatierung):
{
  "brands": ["Marke1", "Marke2"],
  "pricingTier": "premium|volume|budget|unknown",
  "services": ["Service1", "Service2"],
  "slogan": "Der Slogan wenn vorhanden",
  "usp": "Das Alleinstellungsmerkmal",
  "keyPromises": ["Versprechen 1", "Versprechen 2"],
  "strengths": ["Stärke 1", "Stärke 2"],
  "weaknesses": ["Mögliche Schwäche basierend auf fehlenden Features"]
}

Hinweise:
- brands: Auto-Marken die verkauft werden (BMW, VW, Mercedes, Audi, etc.)
- pricingTier: "premium" für Luxusmarken, "volume" für Massenmarkt, "budget" für günstige Anbieter
- services: Werkstatt, Finanzierung, Leasing, Probefahrt, Gebrauchtwagen, etc.
- keyPromises: Marketing-Versprechen wie "Beste Preise", "24h Service", "Familienfreundlich"
- strengths: Was macht dieses Autohaus gut?
- weaknesses: Was fehlt oder könnte besser sein?`
        }]
      }]
    });
    
    const text = response.text || '';
    const jsonMatch = text.match(/\{[\s\S]*\}/);
    
    if (jsonMatch) {
      const parsed = JSON.parse(jsonMatch[0]);
      return {
        brands: parsed.brands || [],
        pricingTier: parsed.pricingTier || 'unknown',
        services: parsed.services || [],
        slogan: parsed.slogan,
        usp: parsed.usp,
        keyPromises: parsed.keyPromises || [],
        strengths: parsed.strengths || [],
        weaknesses: parsed.weaknesses || []
      };
    }
    
    throw new Error('Could not parse AI response');
    
  } catch (error) {
    console.warn('AI analysis failed, using basic extraction:', error);
    return extractBasicInfo(markdown, name);
  }
}

/**
 * Basic info extraction without AI
 */
function extractBasicInfo(markdown: string, name: string): Partial<CompetitorProfile> {
  const content = markdown.toLowerCase();
  
  // Detect brands
  const brands: string[] = [];
  const brandKeywords = ['bmw', 'mercedes', 'audi', 'volkswagen', 'vw', 'porsche', 'toyota', 'honda', 'ford', 'opel', 'skoda', 'seat', 'hyundai', 'kia', 'mazda', 'nissan', 'renault', 'peugeot', 'citroen', 'fiat', 'volvo', 'mini', 'smart', 'tesla'];
  brandKeywords.forEach(brand => {
    if (content.includes(brand)) {
      brands.push(brand.toUpperCase());
    }
  });
  
  // Detect services
  const services: string[] = [];
  const serviceKeywords = {
    'werkstatt': 'Werkstatt',
    'service': 'Service',
    'finanzierung': 'Finanzierung',
    'leasing': 'Leasing',
    'probefahrt': 'Probefahrt',
    'gebrauchtwagen': 'Gebrauchtwagen',
    'neuwagen': 'Neuwagen',
    'reparatur': 'Reparatur',
    'inspektion': 'Inspektion'
  };
  Object.entries(serviceKeywords).forEach(([key, value]) => {
    if (content.includes(key)) {
      services.push(value);
    }
  });
  
  // Detect pricing tier
  let pricingTier: PricingTier = 'unknown';
  const premiumBrands = ['bmw', 'mercedes', 'audi', 'porsche'];
  const hasPremium = premiumBrands.some(b => content.includes(b));
  if (hasPremium) {
    pricingTier = 'premium';
  } else if (content.includes('günstig') || content.includes('billig') || content.includes('preiswert')) {
    pricingTier = 'budget';
  } else if (brands.length > 0) {
    pricingTier = 'volume';
  }
  
  return {
    brands,
    pricingTier,
    services,
    keyPromises: [],
    strengths: [],
    weaknesses: []
  };
}

/**
 * Generate comparison insights with AI
 */
async function generateInsightsWithAI(
  ownProfile: OwnProfile,
  competitors: CompetitorProfile[]
): Promise<CompetitorInsights> {
  try {
    const apiKey = import.meta.env.VITE_GEMINI_API_KEY;
    
    if (!apiKey) {
      return generateBasicInsights(ownProfile, competitors);
    }
    
    const genAI = new GoogleGenAI({ apiKey });
    
    const competitorSummary = competitors.map(c => ({
      name: c.name,
      brands: c.brands,
      services: c.services,
      pricingTier: c.pricingTier,
      usp: c.usp,
      rating: c.googleRating
    }));
    
    const response = await genAI.models.generateContent({
      model: 'gemini-2.0-flash',
      contents: [{
        parts: [{
          text: `Erstelle eine Wettbewerbsanalyse für ein Autohaus.

EIGENES AUTOHAUS:
${JSON.stringify(ownProfile, null, 2)}

WETTBEWERBER (${competitors.length}):
${JSON.stringify(competitorSummary, null, 2)}

Antworte NUR mit einem JSON-Objekt:
{
  "opportunities": ["Chance 1", "Chance 2", "Chance 3"],
  "threats": ["Risiko 1", "Risiko 2"],
  "recommendations": ["Empfehlung 1", "Empfehlung 2", "Empfehlung 3"],
  "summary": "Kurze Zusammenfassung der Wettbewerbssituation in 2-3 Sätzen"
}

Fokussiere auf:
- Lücken im Markt die das eigene Autohaus füllen könnte
- Stärken der Wettbewerber die eine Bedrohung darstellen
- Konkrete, umsetzbare Empfehlungen`
        }]
      }]
    });
    
    const text = response.text || '';
    const jsonMatch = text.match(/\{[\s\S]*\}/);
    
    if (jsonMatch) {
      const parsed = JSON.parse(jsonMatch[0]);
      return {
        opportunities: parsed.opportunities || [],
        threats: parsed.threats || [],
        recommendations: parsed.recommendations || [],
        summary: parsed.summary || ''
      };
    }
    
    throw new Error('Could not parse insights');
    
  } catch (error) {
    console.warn('Insights generation failed:', error);
    return generateBasicInsights(ownProfile, competitors);
  }
}

/**
 * Generate basic insights without AI
 */
function generateBasicInsights(
  ownProfile: OwnProfile,
  competitors: CompetitorProfile[]
): CompetitorInsights {
  const opportunities: string[] = [];
  const threats: string[] = [];
  const recommendations: string[] = [];
  
  // Analyze brand coverage
  const allCompetitorBrands = new Set(competitors.flatMap(c => c.brands));
  const uniqueBrands = ownProfile.brands.filter(b => !allCompetitorBrands.has(b));
  if (uniqueBrands.length > 0) {
    opportunities.push(`Exklusiver Anbieter für: ${uniqueBrands.join(', ')}`);
  }
  
  // Analyze ratings
  const avgCompetitorRating = competitors
    .filter(c => c.googleRating)
    .reduce((sum, c) => sum + (c.googleRating || 0), 0) / competitors.filter(c => c.googleRating).length;
  
  if (ownProfile.googleRating && ownProfile.googleRating < avgCompetitorRating) {
    threats.push(`Google-Bewertung unter dem Durchschnitt der Wettbewerber (${avgCompetitorRating.toFixed(1)})`);
    recommendations.push('Google-Bewertungen aktiv verbessern durch Kundenfeedback-Kampagne');
  }
  
  // Service analysis
  const allServices = new Set(competitors.flatMap(c => c.services));
  const uniqueServices = ownProfile.services.filter(s => !allServices.has(s));
  if (uniqueServices.length > 0) {
    opportunities.push(`Einzigartige Services: ${uniqueServices.join(', ')}`);
  }
  
  return {
    opportunities,
    threats,
    recommendations,
    summary: `${competitors.length} Wettbewerber im Umkreis analysiert.`
  };
}

export interface CompetitorAnalysisContext {
  businessName: string;
  industry: string;
  services: string[];
  usp?: string;
  slogan?: string;
  industryKeywords?: string[];
  businessModel?: 'b2b' | 'b2c' | 'b2b2c' | 'unknown';
}

function isAutomotiveContext(ctx: CompetitorAnalysisContext): boolean {
  const blob = [ctx.industry, ...(ctx.services || []), ...(ctx.industryKeywords || [])]
    .join(' ')
    .toLowerCase();
  return /\b(auto|kfz|fahrzeug|autohaus|autohändler|car dealer|pkw|nutzfahrzeug|gebrauchtwagen|neuwagen)\b/.test(blob);
}

function buildCompetitorSearchKeywords(ctx: CompetitorAnalysisContext, locationHint: string): string[] {
  const out: string[] = [];
  const push = (s: string) => {
    const t = s.trim();
    if (t.length >= 2 && !out.some(x => x.toLowerCase() === t.toLowerCase())) out.push(t);
  };

  if (ctx.industry && ctx.industry !== 'Unknown' && ctx.industry !== 'Unbekannt') {
    push(ctx.industry);
  }
  for (const s of ctx.services || []) {
    if (out.length >= 6) break;
    push(s);
  }
  for (const k of ctx.industryKeywords || []) {
    if (out.length >= 8) break;
    push(k);
  }
  const hint = locationHint.trim();
  if (hint && ctx.industry) {
    push(`${ctx.industry} ${hint}`.slice(0, 100));
  } else if (hint) {
    push(`Unternehmen ${hint}`.slice(0, 100));
  }
  return out;
}

export const competitorAnalysisService = {
  /**
   * Run complete competitor analysis (Legacy Wrapper)
   */
  async runCompetitorAnalysis(
    brandBook: BrandBook,
    address: string,
    radiusKm: number = 40,
    onProgress?: (progress: AnalysisProgress) => void,
    zipCodes?: string[]
  ): Promise<CompetitorComparison> {
    const context: CompetitorAnalysisContext = {
      businessName: brandBook.businessName,
      industry: brandBook.industry,
      services: brandBook.offerings,
      usp: brandBook.usp,
      slogan: brandBook.slogan,
      industryKeywords: brandBook.industryKeywords,
      businessModel: brandBook.businessModel
    };
    
    return this.runAnalysis(context, address, radiusKm, onProgress, zipCodes);
  },

  /**
   * Run analysis with generic context
   */
  async runAnalysis(
    context: CompetitorAnalysisContext,
    address: string,
    radiusKm: number = 40,
    onProgress?: (progress: AnalysisProgress) => void,
    zipCodes?: string[]
  ): Promise<CompetitorComparison> {
    const competitors: CompetitorProfile[] = [];
    
    try {
      if (!placesService.isConfigured()) {
        throw new Error(
          'Google Places API ist nicht konfiguriert. Bitte setzen Sie VITE_GOOGLE_PLACES_API_KEY und aktivieren Sie in der Google Cloud Console die APIs „Geocoding“, „Places API“ und Nearby Search für diesen Schlüssel.'
        );
      }

      let places: any[] = [];

      if (zipCodes && zipCodes.length > 0) {
        // Step 1: Find competitors in ZIP codes
        onProgress?.({
          status: 'finding-competitors',
          currentStep: 1,
          totalSteps: 3,
          message: `Suche Wettbewerber in ${zipCodes.length} PLZ-Gebieten...`
        });

        // Search for dealerships in each selected ZIP area
        const allPlaces: any[] = [];
        const seenPlaceIds = new Set<string>();

        for (const zip of zipCodes) {
          const zipQuery = `Autohaus in PLZ ${zip}, Germany`;
          const results = await placesService.searchPlaces(zipQuery);
          
          for (const place of results) {
            if (!seenPlaceIds.has(place.placeId)) {
              seenPlaceIds.add(place.placeId);
              allPlaces.push(place);
            }
          }
          
          // Small delay to avoid rate limits if many zips
          if (zipCodes.length > 3) {
            await new Promise(resolve => setTimeout(resolve, 200));
          }
        }
        places = allPlaces;
      } else {
        // Step 1: Geocode address
        onProgress?.({
          status: 'geocoding',
          currentStep: 1,
          totalSteps: 4,
          message: 'Adresse wird lokalisiert...'
        });
        
        const location = await placesService.geocodeAddress(address);
        
        if (!location) {
          throw new Error('Adresse konnte nicht gefunden werden. Bitte prüfen Sie Ihre API-Konfiguration (Geocoding API).');
        }
        
        // Step 2: Find competitors
        onProgress?.({
          status: 'finding-competitors',
          currentStep: 2,
          totalSteps: 4,
          message: 'Suche Wettbewerber im Umkreis...'
        });
        
        if (isAutomotiveContext(context)) {
          places = await placesService.findDealershipsNearby(
            location.lat,
            location.lng,
            radiusKm
          );
        } else {
          const keywords = buildCompetitorSearchKeywords(context, address);
          places = await placesService.findNearbyBusinessesByKeywords(
            location.lat,
            location.lng,
            radiusKm,
            keywords.length > 0 ? keywords : [context.industry || 'business services']
          );
        }
      }
      
      console.log(`Found ${places.length} potential competitors`);
      
      // Step 3: Analyze each competitor
      const totalToAnalyze = Math.min(places.length, 10); // Limit to 10
      const startStep = zipCodes ? 2 : 3;
      const totalSteps = zipCodes ? 3 : 4;
      
      for (let i = 0; i < totalToAnalyze; i++) {
        const place = places[i];
        
        onProgress?.({
          status: 'analyzing-competitor',
          currentStep: startStep,
          totalSteps: totalSteps,
          message: `Analysiere ${place.name}... (${i + 1}/${totalToAnalyze})`,
          currentCompetitor: place.name
        });
        
        const profile = await this.analyzeCompetitor(place);
        competitors.push(profile);
      }
      
      // Final Step: Generate comparison
      onProgress?.({
        status: 'generating-comparison',
        currentStep: totalSteps,
        totalSteps: totalSteps,
        message: 'Erstelle Vergleichsanalyse...'
      });
      
      const comparison = await this.generateComparison(context, competitors, zipCodes ? `PLZ: ${zipCodes.join(', ')}` : address, radiusKm);
      
      onProgress?.({
        status: 'complete',
        currentStep: totalSteps,
        totalSteps: totalSteps,
        message: 'Analyse abgeschlossen!'
      });
      
      return comparison;
      
    } catch (error) {
      console.error('Competitor analysis failed:', error);
      
      let errorMessage = (error as Error).message;
      if (errorMessage.includes('REQUEST_DENIED')) {
        errorMessage = 'Zugriff verweigert (REQUEST_DENIED). Bitte stellen Sie sicher, dass die "Geocoding API" und "Places API" in der Google Cloud Console für Ihren API-Key aktiviert sind.';
      }

      onProgress?.({
        status: 'error',
        currentStep: 0,
        totalSteps: 4,
        message: 'Fehler bei der Analyse',
        error: errorMessage
      });
      throw new Error(errorMessage);
    }
  },

  /**
   * Analyze a single competitor from place data
   */
  async analyzeCompetitor(place: any): Promise<CompetitorProfile> {
    const profile: CompetitorProfile = {
      id: place.placeId || `competitor-${Date.now()}`,
      name: place.name,
      address: place.address,
      distanceKm: place.distanceKm,
      website: place.website,
      placeId: place.placeId,
      googleRating: place.rating,
      reviewCount: place.reviewCount,
      phone: place.phone,
      brands: [],
      pricingTier: 'unknown',
      services: [],
      keyPromises: [],
      strengths: [],
      weaknesses: [],
      analyzed: false
    };
    
    // Get detailed place info if needed
    if (place.placeId && !place.website) {
      const details = await placesService.getPlaceDetails(place.placeId);
      if (details) {
        profile.website = details.website;
        profile.phone = details.formattedPhone;
      }
    }
    
    // Deep research via website scraping
    if (profile.website) {
      try {
        console.log(`🔍 Deep research for ${profile.name}: ${profile.website}`);
        
        const scraped = await firecrawlService.scrapeWebsite(profile.website);
        
        const aiAnalysis = await analyzeCompetitorWithAI(
          scraped.markdown,
          scraped.html,
          profile.name
        );
        
        Object.assign(profile, aiAnalysis);
        profile.analyzed = true;
        profile.analyzedAt = new Date();
        
        // Extract logo
        const logoUrl = await firecrawlService.extractRealLogo(profile.website);
        if (logoUrl) {
          profile.logoUrl = logoUrl;
        }
        
      } catch (error) {
        console.warn(`Failed to scrape ${profile.website}:`, error);
        profile.error = (error as Error).message;
      }
    }
    
    return profile;
  },

  /**
   * Generate comparison between own brand and competitors
   */
  async generateComparison(
    context: CompetitorAnalysisContext,
    competitors: CompetitorProfile[],
    centerAddress: string,
    radiusKm: number
  ): Promise<CompetitorComparison> {
    // Build own profile from context
    const ownProfile: OwnProfile = {
      name: context.businessName,
      slogan: context.slogan,
      usp: context.usp,
      brands: context.industryKeywords?.filter(k => 
        ['bmw', 'mercedes', 'audi', 'vw', 'volkswagen', 'porsche', 'toyota', 'honda', 'ford', 'opel'].some(b => 
          k.toLowerCase().includes(b)
        )
      ) || [],
      services: context.services,
      pricingTier: context.businessModel === 'b2b' ? 'premium' : 'volume'
    };
    
    // Build comparison matrix
    const comparisonMatrix: ComparisonRow[] = [
      {
        category: 'Marken',
        categoryKey: 'brands',
        ownValue: ownProfile.brands.join(', ') || '-',
        competitorValues: competitors.map(c => ({
          name: c.name,
          value: c.brands.join(', ') || '-'
        }))
      },
      {
        category: 'Services',
        categoryKey: 'services',
        ownValue: ownProfile.services.slice(0, 3).join(', ') || '-',
        competitorValues: competitors.map(c => ({
          name: c.name,
          value: c.services.slice(0, 3).join(', ') || '-'
        }))
      },
      {
        category: 'Preisniveau',
        categoryKey: 'pricing',
        ownValue: this.formatPricingTier(ownProfile.pricingTier),
        competitorValues: competitors.map(c => ({
          name: c.name,
          value: this.formatPricingTier(c.pricingTier)
        }))
      },
      {
        category: 'USP',
        categoryKey: 'usp',
        ownValue: ownProfile.usp || '-',
        competitorValues: competitors.map(c => ({
          name: c.name,
          value: c.usp || '-'
        }))
      },
      {
        category: 'Google Bewertung',
        categoryKey: 'rating',
        ownValue: '-', // Could be added from Google My Business
        competitorValues: competitors.map(c => ({
          name: c.name,
          value: c.googleRating ? `${c.googleRating} ⭐ (${c.reviewCount})` : '-'
        }))
      }
    ];
    
    // Generate insights
    const insights = await generateInsightsWithAI(ownProfile, competitors);
    
    return {
      id: `analysis-${Date.now()}`,
      ownProfile,
      competitors,
      comparisonMatrix,
      insights,
      analyzedAt: new Date(),
      radiusKm,
      centerAddress,
      competitorCount: competitors.length
    };
  },

  /**
   * Format pricing tier for display
   */
  formatPricingTier(tier: PricingTier): string {
    const labels: Record<PricingTier, string> = {
      'premium': 'Premium',
      'volume': 'Volumen',
      'budget': 'Budget',
      'unknown': 'Unbekannt'
    };
    return labels[tier];
  },

  /**
   * Save competitor analysis to Firestore
   */
  async saveAnalysis(
    orgId: string,
    brandBookId: string,
    comparison: CompetitorComparison,
    userId: string
  ): Promise<string> {
    const analysesRef = collection(
      db, 
      'organizations', orgId, 
      'brandBooks', brandBookId, 
      'competitorAnalyses'
    );
    const docRef = doc(analysesRef);
    
    const savedAnalysis: SavedCompetitorAnalysis = {
      id: docRef.id,
      brandBookId,
      createdAt: new Date(),
      updatedAt: new Date(),
      createdBy: userId,
      radiusKm: comparison.radiusKm,
      centerAddress: comparison.centerAddress,
      competitorCount: comparison.competitorCount,
      competitors: comparison.competitors,
      comparison
    };
    
    await setDoc(docRef, {
      ...savedAnalysis,
      createdAt: Timestamp.fromDate(savedAnalysis.createdAt),
      updatedAt: Timestamp.fromDate(savedAnalysis.updatedAt),
      comparison: {
        ...comparison,
        analyzedAt: Timestamp.fromDate(comparison.analyzedAt)
      }
    });
    
    console.log(`✅ Competitor analysis saved: ${docRef.id}`);
    return docRef.id;
  },

  /**
   * Get saved analyses for a brand book
   */
  async getSavedAnalyses(
    orgId: string,
    brandBookId: string
  ): Promise<SavedCompetitorAnalysis[]> {
    try {
      const analysesRef = collection(
        db,
        'organizations', orgId,
        'brandBooks', brandBookId,
        'competitorAnalyses'
      );
      const q = query(analysesRef, orderBy('createdAt', 'desc'), limit(10));
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          ...data,
          id: doc.id,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
          comparison: {
            ...data.comparison,
            analyzedAt: data.comparison?.analyzedAt?.toDate() || new Date()
          }
        } as SavedCompetitorAnalysis;
      });
      
    } catch (error) {
      console.error('Error loading saved analyses:', error);
      return [];
    }
  },

  /**
   * Get a single saved analysis
   */
  async getAnalysis(
    orgId: string,
    brandBookId: string,
    analysisId: string
  ): Promise<SavedCompetitorAnalysis | null> {
    try {
      const docRef = doc(
        db,
        'organizations', orgId,
        'brandBooks', brandBookId,
        'competitorAnalyses', analysisId
      );
      const snapshot = await getDoc(docRef);
      
      if (!snapshot.exists()) {
        return null;
      }
      
      const data = snapshot.data();
      return {
        ...data,
        id: snapshot.id,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
        comparison: {
          ...data.comparison,
          analyzedAt: data.comparison?.analyzedAt?.toDate() || new Date()
        }
      } as SavedCompetitorAnalysis;
      
    } catch (error) {
      console.error('Error loading analysis:', error);
      return null;
    }
  }
};
